import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Backdrop,
  CircularProgress,
  Chip,
  Stack,
  Container,
  Fade,
  Slide,
  IconButton,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  PlayArrow as ActiveIcon,
} from '@mui/icons-material';
import client from '../../API';
import { NewTracker } from './newTracker';
import { Tracker } from './tracker';

const Trackers = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [open, setOpen] = useState(false);
  const [openTracker, setOpenTracker] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedTrackers, setSelectedTrackers] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  // Fetch trackers using react-query
  const {
    data: trackers,
    isLoading,
    error,
    refetch,
  } = useQuery('trackers', client.trackers.fetch, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle open and close of dialog
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenTracker = (tracker) => {
    setSelectedTracker(tracker);
    setOpenTracker(true);
  };

  const handleCloseTracker = () => {
    setOpenTracker(false);
  };

  const { mutate: postTracker, isLoading: postTrackerLoading } = useMutation({
    mutationFn: client.trackers.create,
    onSuccess: () => {
      refetch();
      handleClose();
    },
    onError: (error) => {
      console.error('Error creating tracker:', error);
      alert(`Network error: ${error?.response?.data?.error || error.message}`);
    },
  });

  const handleAddTracker = (tracker) => {
    postTracker(tracker);
  };

  // Filter trackers by active tab
  const filteredTrackers = trackers?.filter((tracker) => {
    if (activeTab === 'all') return true;
    return tracker.status.toLowerCase() === activeTab;
  });

  // Handle tracker selection
  const handleTrackerSelect = (trackerId) => {
    setSelectedTrackers((prevSelected) => {
      if (prevSelected.includes(trackerId)) {
        return prevSelected.filter((id) => id !== trackerId);
      }
      return [...prevSelected, trackerId];
    });
  };

  // Handle applying status changes to selected trackers
  const handleApplyChanges = async () => {
    if (!newStatus) {
      alert('Please select a new status');
      return;
    }
    setUpdateLoading(true);
    try {
      await Promise.all(
        selectedTrackers.map((id) => client.trackers.update(id, { status: newStatus }))
      );
      refetch();
      setSelectedTrackers([]);
      setNewStatus('');
    } catch (error) {
      console.error('Error updating trackers:', error);
      alert(`Network error: ${error?.response?.data?.error || error.message}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <ActiveIcon fontSize="small" />;
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Paper 
          elevation={0} 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 4,
            p: 4,
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h3" 
              fontWeight="700" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Content Trackers
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9,
                fontSize: { xs: '1rem', md: '1.25rem' },
                mb: 3
              }}
            >
              Manage and monitor your content tracking campaigns
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button 
                variant="contained" 
                size="large"
                onClick={handleOpen}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Add New Tracker
              </Button>
              
              <FormControl 
                sx={{ 
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.8)',
                  },
                  '& .MuiSelect-select': {
                    color: 'white',
                  },
                  '& .MuiSelect-icon': {
                    color: 'rgba(255,255,255,0.8)',
                  },
                }}
              >
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  startAdornment={<FilterIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.8)' }} />}
                >
                  <MenuItem value="All">All Trackers</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {/* Tabs Section */}
      <Slide in direction="up" timeout={600}>
        <Paper 
          elevation={2} 
          sx={{ 
            borderRadius: 3,
            mb: 4,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            centered
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                py: 2,
                '&.Mui-selected': {
                  color: '#667eea',
                },
              },
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  All
                  <Chip size="small" label={trackers?.length || 0} />
                </Box>
              } 
              value="all" 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ActiveIcon fontSize="small" />
                  Active
                  <Chip size="small" label={trackers?.filter(t => t.status.toLowerCase() === 'active').length || 0} />
                </Box>
              } 
              value="active" 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Completed
                  <Chip size="small" label={trackers?.filter(t => t.status.toLowerCase() === 'completed').length || 0} />
                </Box>
              } 
              value="completed" 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArchiveIcon fontSize="small" />
                  Archived
                  <Chip size="small" label={trackers?.filter(t => t.status.toLowerCase() === 'archived').length || 0} />
                </Box>
              } 
              value="archived" 
            />
          </Tabs>
        </Paper>
      </Slide>

      {/* Bulk Actions */}
      {selectedTrackers.length > 0 && (
        <Slide in direction="down" timeout={400}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                {selectedTrackers.length} tracker{selectedTrackers.length > 1 ? 's' : ''} selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>New Status</InputLabel>
                  <Select
                    value={newStatus}
                    label="New Status"
                    onChange={(e) => setNewStatus(e.target.value)}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      '& .MuiSelect-select': { color: 'white' },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Archived">Archived</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="contained" 
                  onClick={handleApplyChanges}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                    },
                  }}
                >
                  Apply Changes
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Slide>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading trackers
          </Typography>
          <Typography color="text.secondary">
            {error.message}
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!isLoading && filteredTrackers?.length === 0 && (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}
        >
          <Typography variant="h5" gutterBottom fontWeight={600}>
            No trackers found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {activeTab === 'all' 
              ? "You haven't created any trackers yet. Get started by adding your first tracker!"
              : `No ${activeTab} trackers found. Try switching to a different tab.`
            }
          </Typography>
          {activeTab === 'all' && (
            <Button 
              variant="contained" 
              onClick={handleOpen}
              startIcon={<AddIcon />}
              size="large"
              sx={{ borderRadius: 3 }}
            >
              Create Your First Tracker
            </Button>
          )}
        </Paper>
      )}

      {/* Trackers Grid */}
      <Grid container spacing={3}>
        {(filteredTrackers || []).map((tracker, index) => (
          <Grid item xs={12} sm={6} lg={4} key={tracker.id}>
            <Fade in timeout={600} style={{ transitionDelay: `${index * 100}ms` }}>
              <Card
                onClick={() => handleOpenTracker(tracker)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    '& .card-media': {
                      transform: 'scale(1.1)',
                    },
                  },
                }}
              >
                {/* Selection Checkbox */}
                <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrackerSelect(tracker.id);
                    }}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,1)',
                      },
                    }}
                  >
                    <Checkbox
                      checked={selectedTrackers.includes(tracker.id)}
                      sx={{ p: 0 }}
                    />
                  </IconButton>
                </Box>

                {/* Status Badge */}
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                  <Chip
                    icon={getStatusIcon(tracker.status)}
                    label={tracker.status}
                    size="small"
                    color={getStatusColor(tracker.status)}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(10px)',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {/* Tracker Photo */}
                {tracker.tracker_photo ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={tracker.tracker_photo}
                    alt={tracker.name}
                    className="card-media"
                    sx={{
                      transition: 'transform 0.3s ease',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    className="card-media"
                    sx={{
                      height: 200,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <Typography variant="h3" color="white" fontWeight={700}>
                      {tracker.name.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}

                <CardContent sx={{ p: 3 }}>
                  <Typography 
                    variant="h6" 
                    fontWeight={700} 
                    gutterBottom
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tracker.name}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {tracker.description}
                  </Typography>

                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>
                        Links
                      </Typography>
                      <Badge 
                        badgeContent={tracker.links.length} 
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            bgcolor: '#667eea',
                          },
                        }}
                      >
                        <Box sx={{ width: 20, height: 20 }} />
                      </Badge>
                    </Box>
                    
                    {tracker.stop_tracking_at && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="Tracking stopped"
                        sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                      />
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Created {new Date(tracker.date_created).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {/* Dialogs */}
      <NewTracker
        open={open}
        handleAddTracker={handleAddTracker}
        handleClose={handleClose}
      />
      <Tracker
        open={openTracker}
        handleClose={handleCloseTracker}
        tracker={selectedTracker}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.modal + 10,
          backdropFilter: 'blur(5px)',
        }}
        open={postTrackerLoading || updateLoading}
      >
        <CircularProgress 
          color="inherit" 
          size={60}
          thickness={4}
        />
      </Backdrop>
    </Container>
  );
};

export default Trackers;